import { createApi } from '@reduxjs/toolkit/query/react';

import {
  AuthenticationEndpoints as Endpoints,
  type AuthenticateByCookieRequest,
  type AuthenticateByCookieResponse,
  type AuthenticateByEmailAccessCodeRequest,
  type AuthenticateByEmailAccessCodeResponse,
  type AuthenticateByEmailRequest,
  type AuthenticateByEmailResponse,
  type AuthenticateByPasswordRequest,
  type AuthenticateByPasswordResponse,
  type AuthenticateBySecretRequest,
  type AuthenticateBySecretResponse,
  type DecryptEmailCookieRequest,
  type DecryptEmailCookieResponse,
  type PrepareAuthenticateRequest,
  type PrepareAuthenticateResponse,
  type SwitchOrganizationRequest,
  type SwitchOrganizationResponse,
} from '@app/api/Authentication/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { AUTHENTICATION } from '@app/constants';

export const authenticationApi = createApi({
  reducerPath: 'authenticationApi',
  baseQuery: createApiBaseQuery(AUTHENTICATION),
  endpoints: ({ mutation }) => ({
    [Endpoints.PrepareAuthenticate]: mutation<
      PrepareAuthenticateResponse,
      PrepareAuthenticateRequest
    >({
      // Тело — пустой объект, а не отсутствие тела: новый бэк параметров не
      // читает, а прежний на запрос без тела отвечал 500 (415 Unsupported
      // Media Type — проверено на dev 07.09.2026). Так фронт работает с обеими
      // версиями бэка в любом порядке выката (us/11114).
      query: () => ({
        url: Endpoints.PrepareAuthenticate,
        method: Methods.POST,
        data: {},
      }),
    }),
    [Endpoints.AuthenticateByCookie]: mutation<
      AuthenticateByCookieResponse,
      AuthenticateByCookieRequest
    >({
      query: (data) => ({
        url: Endpoints.AuthenticateByCookie,
        method: Methods.POST,
        data,
      }),
    }),
    [Endpoints.AuthenticateByEmail]: mutation<
      AuthenticateByEmailResponse,
      AuthenticateByEmailRequest
    >({
      query: (data) => ({
        url: Endpoints.AuthenticateByEmail,
        method: Methods.POST,
        data,
      }),
    }),
    [Endpoints.AuthenticateByEmailAccessCode]: mutation<
      AuthenticateByEmailAccessCodeResponse,
      AuthenticateByEmailAccessCodeRequest
    >({
      query: (data) => ({
        url: Endpoints.AuthenticateByEmailAccessCode,
        method: Methods.POST,
        data,
      }),
    }),
    [Endpoints.DecryptEmailCookie]: mutation<
      DecryptEmailCookieResponse,
      DecryptEmailCookieRequest
    >({
      query: (data) => ({
        url: Endpoints.DecryptEmailCookie,
        method: Methods.POST,
        data,
      }),
    }),
    [Endpoints.AuthenticateByPassword]: mutation<
      AuthenticateByPasswordResponse,
      AuthenticateByPasswordRequest
    >({
      query: (data) => ({
        url: Endpoints.AuthenticateByPassword,
        method: Methods.POST,
        data,
      }),
    }),
    [Endpoints.AuthenticateBySecret]: mutation<
      AuthenticateBySecretResponse,
      AuthenticateBySecretRequest
    >({
      query: (params) => ({
        url: Endpoints.AuthenticateBySecret,
        method: Methods.POST,
        params,
      }),
    }),
    // Переключение вошедшего пользователя на другую его организацию (US 10910).
    // Возвращает новую ModelAuthentication (свежие токен/cookie, БД, филиал,
    // права) — как полноценный повторный вход.
    [Endpoints.SwitchOrganization]: mutation<
      SwitchOrganizationResponse,
      SwitchOrganizationRequest
    >({
      query: (params) => ({
        url: Endpoints.SwitchOrganization,
        method: Methods.POST,
        params,
      }),
    }),
  }),
});

export const {
  usePrepareAuthenticateMutation,
  useAuthenticateByCookieMutation,
  useAuthenticateByEmailMutation,
  useAuthenticateByEmailAccessCodeMutation,
  useDecryptEmailCookieMutation,
  useAuthenticateByPasswordMutation,
  useAuthenticateBySecretMutation,
  useSwitchOrganizationMutation,
} = authenticationApi;
